/**
 * 레벨별 누적 경험치 표 — **자동 생성 파일. 직접 고치지 말 것.**
 *
 * 서버는 `S_Exp`(exp_update)로 현재 레벨과 누적 경험치만 보내고, 다음 레벨까지
 * 필요한 양은 보내지 않는다 — 원본 클라이언트가 같은 표를 자기 리소스에 들고
 * 있기 때문이다. 진행률(%)을 그리려면 클라이언트도 이 표를 가져야 한다.
 *
 * 근거: 서버 소스 `src/jp/l1j/server/datatables/ExpTable.java`의 `_expTable`.
 *
 * 재생성: python3 scripts/gen_exp_table.py
 */

/** 최대 레벨. 서버 `ExpTable.MAX_LEVEL`과 같다. */
export const MAX_LEVEL = 99;

/**
 * 누적 경험치 표. `EXP_TABLE[level - 1]`이 그 레벨이 되는 데 필요한 누적 경험치다
 * (인덱스 0 = Lv.1 = 0).
 *
 * 길이는 100 — MAX_LEVEL(99)보다 한 칸 많다. 마지막 칸은 서버가
 * `getNeedExpNextLevel(99)`를 계산하려고 두는 보초값이며, 유효 경험치의 상한
 * (`ExpTable.EXP_RANGE` = 0x6ecf16da)과 같은 값이다.
 */
export const EXP_TABLE: readonly number[] = [
  0, 125, 300, 500, 750, 1296, // Lv.1~6
  2401, 4096, 6581, 10000, 14661, 20756, // Lv.7~12
  28581, 38436, 50645, 65556, 83541, 104996, // Lv.13~18
  130341, 160020, 194501, 234276, 279861, 331792, // Lv.19~24
  390641, 456992, 531457, 614672, 707297, 810016, // Lv.25~30
  923537, 1048592, 1185937, 1336352, 1500641, 1679632, // Lv.31~36
  1874177, 2085152, 2313457, 2560016, 2825777, 3111713, // Lv.37~42
  3418818, 3748113, 4100642, 4830002, 6338418, 9833681, // Lv.43~48
  19745870, 55810962, 91876054, 127941146, 164006238, 200071330, // Lv.49~54
  236136422, 272201514, 308266606, 344331698, 380396790, 416461882, // Lv.55~60
  452526974, 488592066, 524657158, 560722250, 596787342, 632852434, // Lv.61~66
  668917526, 704982618, 741047710, 777112802, 813177894, 849242986, // Lv.67~72
  885308078, 921373170, 957438262, 993503354, 1029568446, 1065633538, // Lv.73~78
  1101698630, 1137763722, 1173828814, 1209893906, 1245958998, 1282024090, // Lv.79~84
  1318089182, 1354154274, 1390219366, 1426284458, 1462349550, 1498414642, // Lv.85~90
  1534479734, 1570544826, 1606609918, 1642675010, 1678740102, 1714805194, // Lv.91~96
  1750870286, 1786935378, 1823000470, 1859065562, // Lv.97~100
];

/** 진행률 계산 결과. */
export interface LevelProgress {
  /** 현재 레벨 안에서 쌓은 경험치. */
  current: number;
  /** 이 레벨을 넘기는 데 필요한 총 경험치. */
  needed: number;
  /** 0~1 진행률. */
  ratio: number;
}

/**
 * 그 레벨이 되는 데 필요한 누적 경험치. 표 범위를 벗어나면 양끝 값으로 잘라 준다.
 */
export function getExpByLevel(level: number): number {
  if (!Number.isFinite(level)) return 0;
  const index = Math.floor(level) - 1;
  if (index <= 0) return EXP_TABLE[0];
  if (index >= EXP_TABLE.length) return EXP_TABLE[EXP_TABLE.length - 1];
  return EXP_TABLE[index];
}

/**
 * 현재 레벨과 누적 경험치로 다음 레벨까지의 진행률을 구한다.
 *
 * 서버 `ExpTable.getExpPercentage()`와 같은 계산이다:
 *   (exp - 이번 레벨 누적) / (다음 레벨 누적 - 이번 레벨 누적)
 *
 * 경계 처리:
 *   - 레벨이 1 미만이거나 값이 이상하면 Lv.1로 본다.
 *   - 최대 레벨(그리고 표 범위 밖)은 더 올라갈 곳이 없으므로 100%로 고정한다.
 *   - 필요 경험치가 0 이하로 나오면(표가 깨진 경우) 0%로 떨어뜨려 0 나눗셈을 피한다.
 *   - 누적 경험치가 구간을 벗어나도 0~1 안으로 잘라 낸다.
 */
export function getLevelProgress(level: number, exp: number): LevelProgress {
  const safeLevel = Math.max(1, Number.isFinite(level) ? Math.floor(level) : 1);
  const safeExp = Number.isFinite(exp) ? exp : 0;

  // 만렙 이상 — 다음 레벨이 없으니 100%로 둔다.
  if (safeLevel >= MAX_LEVEL || safeLevel >= EXP_TABLE.length) {
    return { current: 0, needed: 0, ratio: 1 };
  }

  const base = getExpByLevel(safeLevel);
  const next = getExpByLevel(safeLevel + 1);
  const needed = next - base;
  if (needed <= 0) {
    return { current: 0, needed: 0, ratio: 0 };
  }

  const current = Math.min(Math.max(safeExp - base, 0), needed);
  return { current, needed, ratio: current / needed };
}
